import React, { useState } from 'react';
import { XIcon, ExclamationIcon } from '@heroicons/react/solid';

const PRICING = {
  91: 30,
  181: 50,
  365: 60
};

const DURATION_LABELS = {
  91: '91 days (~3 months)',
  181: '181 days (~6 months)',
  365: '365 days (1 year)'
};

function ExtendRenewModal({
  isOpen,
  onClose,
  onConfirm,
  jobData,
  isExpired,
  legacyBalance
}) {
  const [selectedDuration, setSelectedDuration] = useState(91);
  const [processing, setProcessing] = useState(false);

  if (!isOpen) return null;

  const isRenew = isExpired;
  const modalTitle = isRenew ? 'Renew Job Posting' : 'Extend Job Posting';

  // Calculate dates
  const calculateExpiryDate = (days, fromDate = new Date()) => {
    return new Date(fromDate.getTime() + (days + 1) * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const today = new Date();
  const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const newPostedDate = isRenew ? todayString : jobData.postedOn;
  const newExpiryDate = isRenew
    ? calculateExpiryDate(selectedDuration, new Date())
    : calculateExpiryDate(selectedDuration, new Date(jobData.postExpiry));

  // Calculate pricing
  const boardCount = jobData.postAt?.length || 0;
  const pricePerBoard = PRICING[selectedDuration];
  const total = boardCount * pricePerBoard;
  const balanceApplied = Math.min(legacyBalance, total);
  const amountDue = Math.max(0, total - balanceApplied);

  const handleConfirm = async () => {
    setProcessing(true);
    try {
      await onConfirm(selectedDuration, amountDue, balanceApplied);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="fixed z-10 inset-0 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen sm:pt-4 sm:px-4 sm:pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-white sm:rounded-lg px-4 pt-5 pb-4 text-left overflow-y-auto shadow-xl transform transition-all min-h-screen sm:min-h-0 w-full sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
              onClick={onClose}
            >
              <XIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="w-full mt-3 text-center sm:mt-0 sm:text-left">
              <h3 className="text-lg leading-6 font-bold text-gray-900 mb-6">
                {modalTitle}
              </h3>

              {/* Warning for Renew */}
              {isRenew && (
                <div className="mb-6 bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
                  <div className="flex">
                    <ExclamationIcon className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-yellow-900 mb-2">Important: Renewing Updates Posted Date</h4>
                      <p className="text-sm text-yellow-800">
                        Renewing will update the "Posted On" date to <strong>{formatDate(todayString)}</strong>, making this appear as a fresh posting. This helps attract new applicants.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Duration Selection */}
              <fieldset className="mb-6">
                <legend className="text-base font-medium text-gray-900 mb-4">
                  Select Duration
                </legend>
                <div className="space-y-3">
                  <label className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:border-primary transition-colors">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="duration"
                        value="91"
                        checked={selectedDuration === 91}
                        onChange={(e) => setSelectedDuration(Number(e.target.value))}
                        className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                      />
                      <span className="ml-3">
                        <span className="block font-medium text-gray-900">{DURATION_LABELS[91]}</span>
                        <span className="block text-xs text-gray-500">Standard</span>
                      </span>
                    </div>
                    <span className="font-semibold text-primary">$30/board</span>
                  </label>

                  <label className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:border-primary transition-colors">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="duration"
                        value="181"
                        checked={selectedDuration === 181}
                        onChange={(e) => setSelectedDuration(Number(e.target.value))}
                        className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                      />
                      <span className="ml-3">
                        <span className="block font-medium text-gray-900">{DURATION_LABELS[181]}</span>
                        <span className="block text-xs text-gray-500">Most popular</span>
                      </span>
                    </div>
                    <span className="font-semibold text-primary">$50/board</span>
                  </label>

                  <label className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:border-primary transition-colors">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="duration"
                        value="365"
                        checked={selectedDuration === 365}
                        onChange={(e) => setSelectedDuration(Number(e.target.value))}
                        className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                      />
                      <span className="ml-3">
                        <span className="block font-medium text-gray-900">{DURATION_LABELS[365]}</span>
                        <span className="block text-xs text-gray-500">Best Value</span>
                      </span>
                    </div>
                    <span className="font-semibold text-primary">$60/board</span>
                  </label>
                </div>
              </fieldset>

              {/* Job Details & Updated Dates Section */}
              <div className="mb-6 bg-red-50 border-2 border-red-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Job Details</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Job Title</span>
                    <span className="text-sm font-medium text-gray-900">{jobData.title}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Number of Job Boards</span>
                    <span className="text-sm font-medium text-gray-900">{boardCount} board{boardCount > 1 ? 's' : ''}</span>
                  </div>
                  {!isRenew && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Current Expiry</span>
                      <span className="text-sm font-medium text-gray-900">{formatDate(jobData.postExpiry)}</span>
                    </div>
                  )}
                  <div className="border-t border-red-200 my-2 pt-2">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Posted On</span>
                      <span className={`text-sm font-medium ${isRenew ? 'text-primary' : 'text-gray-900'}`}>
                        {formatDate(newPostedDate)}
                        {isRenew && <span className="ml-2 text-xs">(Updated)</span>}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">New Expiry Date</span>
                      <span className="text-sm font-medium text-primary">
                        {formatDate(newExpiryDate)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pricing Breakdown */}
              <div className="bg-red-50 border-2 border-red-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Pricing Breakdown</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-gray-700">
                    <span>{boardCount} board{boardCount > 1 ? 's' : ''} × ${pricePerBoard}</span>
                    <span className="font-medium">${total.toFixed(2)}</span>
                  </div>
                  {balanceApplied > 0 && (
                    <div className="flex justify-between text-primary font-medium">
                      <span>Account balance</span>
                      <span>-${balanceApplied.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-primary pt-3 border-t-2 border-red-200 mt-2 text-lg">
                    <span>Amount Due</span>
                    <span>${amountDue.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-6 sm:flex sm:flex-row-reverse gap-3">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-red-700 focus:outline-none sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleConfirm}
                  disabled={processing}
                >
                  {processing ? 'Processing...' : amountDue > 0 ? 'Proceed to Payment' : `Confirm ${isRenew ? 'Renewal' : 'Extension'}`}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:w-auto sm:text-sm"
                  onClick={onClose}
                  disabled={processing}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExtendRenewModal;